import { Component, CSP_NONCE, effect, ElementRef, inject, OnInit, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

// Services
import { DataService } from 'src/app/services/data.service';
import { WaitErrorDialogsService } from 'src/app/services/wait-error-dialogs.service';
import { ValidateEmail, ValidatePassword, ValidatePassword2, ValidateString } from 'src/app/services/validation.service';

// Constants
import { CAPTCHA_SITE_KEY, LoginState, PRIVACY_POLICY_URL, ResetMode, TERMS_OF_USE_URL } from 'src/app/constants/login.constants';
import { DIALOG_TYPES, RESPONSE_STATUS, SMS_OPTIONS } from 'src/app/constants';
import { ClientService } from 'src/app/services/client.service';
import { ApiService } from 'src/app/services/api.service';
import { API_ENDPOINT } from 'src/app/constants/api-endpoint.constants';
import { environment } from 'src/environments/environment';
import { sendCoralogixCustomEvent, type CoralogixCustomLogSeverity } from 'src/app/utils/coralogix.util';
import { RecaptchaModule } from 'ng-recaptcha-2';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ReactiveFormsModule, RecaptchaModule]
})
export class LoginComponent implements OnInit {
  private fb = inject(FormBuilder);
  private ds = inject(DataService);
  private weds = inject(WaitErrorDialogsService);
  private router = inject(Router);
  private clientService = inject(ClientService);
  private apiService = inject(ApiService);
  public readonly cspNonce = inject(CSP_NONCE);

  private readonly loginTrace = this.readLoginTrace();

  // Loading and State
  isLoading: boolean = true;
  hasSubdomain: boolean = false;
  isUserAuthorised: boolean = true;
  state: LoginState = LoginState.LOGIN;
  customLoader: string = '';

  // Form Controls
  loginForm: FormGroup;
  resetMode: ResetMode = ResetMode.OTP;

  // SSO (Single Sign-On)
  useSSO: string = SMS_OPTIONS.NO;
  forceSSO: string = SMS_OPTIONS.NO;
  loginType: string = 'Prismforce Password Only';
  ssoType: string = '';
  seamlessSSO: string = '';
  oauthClientId: string = '';
  ssoEntryPoint: string = '';

  // Temporary User Info
  tempUserId: string = '';
  tempClientId: string = '';

  // Client Logos
  clientLogoRect: string = '';
  clientLogoSquare: string = '';

  // CAPTCHA
  siteKey: string = CAPTCHA_SITE_KEY;
  captcha: string = '';
  showCaptchaError: boolean = false;
  isCaptchaEnabled: string = SMS_OPTIONS.NO;

  // ViewChild
  public readonly googleDiv = viewChild<ElementRef<HTMLDivElement>>('google');

  protected readonly user = this.clientService.user;
  protected readonly privacyPolicyUrl = PRIVACY_POLICY_URL;
  protected readonly termsOfUseUrl = TERMS_OF_USE_URL;

  constructor() {
    effect(() => {
      const user = this.clientService.user();
      if (user) {
        this.isLoading = false;
        // Post-login navigation (redirectUrl, getRoute, etc.) runs in ClientService.setLoginData
        // after loginData.set — avoid a second navigateByUrl here, which races setLoginData and
        // can resolve false and incorrectly send users to pageNotFound.
      }
    });
  }

  ngOnInit(): void {
    if (this.clientService.user()) {
      return;
    }
    this.initLoginForm();
    this.checkSubdomain();
    this.handleSSOEvent();
  }

  private initLoginForm(): void {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, ValidateEmail]],
      otp: ['', [ValidateString(6)]],
      oldPassword: ['', [ValidatePassword]],
      password: ['', [Validators.required, ValidatePassword]],
      password2: ['', [Validators.required, ValidatePassword2("password")]],
      sso: [false],
    });
  }

  private checkSubdomain(): void {
    this.hasSubdomain = window.location.hostname.split('.').length >= 3 && window.location.hostname.split('.')[0] !== 'pf';
  }

  private handleSSOEvent(): void {
    const { type: eventType, code: eventCode, useSeamlessSSO = false, logoRect = "", logoSquare = "", clientName } = this.router.parseUrl(this.router.url).queryParams;
    const initialLoginData = this.clientService.initialLoginData;
    const seamlessSamlFail = eventType === 'saml2LoginFail' && useSeamlessSSO?.toString?.() === 'true';
    if (this.loginTrace.seq >= 2 || initialLoginData || eventType) {
      this.logLogin('init', {
        eventType: eventType ?? null,
        eventCode: eventCode ?? null,
        seamlessSamlFail,
        hasInitialLoginData: Boolean(initialLoginData),
        initialHasUser: Boolean(initialLoginData?.user),
        initialSsoType: initialLoginData?.ssoType ?? null,
        initialSeamlessSSO: initialLoginData?.seamlessSSO ?? null,
        searchKeys: [...new URLSearchParams(window.location.search).keys()],
      }, seamlessSamlFail ? 'error' : 'warn');
    }
    if (eventType === 'saml2LoginFail') {
      if (seamlessSamlFail) {
        this.isUserAuthorised = false;
        this.clientLogoRect = logoRect;
        this.clientLogoSquare = logoSquare;
        this.clientService.updateTitleLogo(logoSquare || logoRect, clientName || "Prismforce");
        return;
      } else {
        this.weds.showDialog({ type: DIALOG_TYPES.GENERIC, code: parseInt(eventCode) });
      }
    }
    if (initialLoginData) {
      this.handleAutoLoginSuccess(initialLoginData);
    }
  }

  private handleAutoLoginSuccess(rs: any): void {
    ['forceSSO', 'ssoType', 'oauthClientId', 'ssoEntryPoint', 'loginType', 'isCaptchaEnabled', 'seamlessSSO'].forEach((key) => this[key] = rs[key]);
    this.clientLogoRect = rs.logoRect;
    this.clientLogoSquare = rs.logoSquare;
    const willAutoSubmit = this.ssoType === 'ADFS' && this.seamlessSSO === SMS_OPTIONS.YES;
    if (this.ssoType === 'Google Identity') this.initGoogleIdentity();
    if (willAutoSubmit) {
      this.logLogin('seamless-auto-submit', {
        ssoType: this.ssoType,
        seamlessSSO: this.seamlessSSO,
        forceSSO: this.forceSSO,
        loginType: this.loginType,
      }, 'error');
      this.onLoginClick('seamless-sso');
    }
    this.checkUrlForLoginParams();
    this.isLoading = false;
  }

  checkUrlForLoginParams(): void {
    const params = this.router.url.split('&pw=');
    if (params.length > 1) {
      const email = atob(params[0].replace(/^\/*\?*id=/, ''));
      const pw = atob(params[1].replace(/(\/+)$/i, ''));
      if (email && pw) {
        this.loginForm.controls.email.setValue(email);
        this.loginForm.controls.password.setValue(pw);
        this.onLoginClick('url-params');
      }
    }
  }

  onForgotClick(): void {
    this.resetForm(this.state === LoginState.LOGIN ? LoginState.FORGOT_PASSWORD : LoginState.LOGIN);
  }

  resetForm(newState?: LoginState): void {
    if (newState) this.state = newState;
    setTimeout(() => {
      this.loginForm.reset();
      this.loginForm.controls.email.setErrors(null);
      this.loginForm.controls.password.setErrors(null);
      this.loginForm.controls.password2.setErrors(null);
      this.loginForm.controls.otp.setErrors(null);
    }, 10);
  }

  onSSOChange(event: any): void {
    this.useSSO = event.target?.checked ? SMS_OPTIONS.YES : SMS_OPTIONS.NO;
    localStorage.setItem('requestedApp', 'sms');
  }

  onLoginClick(trigger: 'user' | 'seamless-sso' | 'url-params' = 'user'): void {
    if (this.state === LoginState.LOGIN) {
      this.handleLogin(trigger);
    } else if (this.state === LoginState.FORGOT_PASSWORD) {
      this.handleForgotPassword();
    } else {
      this.handleResetPassword();
    }
  }

  private handleLogin(trigger: 'user' | 'seamless-sso' | 'url-params'): void {
    const emailControl = this.loginForm?.controls?.email;
    const passwordControl = this.loginForm?.controls?.password;
    const context = {
      trigger,
      hasEmail: Boolean(emailControl?.value),
      hasPassword: Boolean(passwordControl?.value),
      emailInvalid: Boolean(emailControl?.invalid),
      passwordInvalid: Boolean(passwordControl?.invalid),
      captchaPresent: Boolean(this.captcha),
      isCaptchaEnabled: this.isCaptchaEnabled,
    };

    if (this.loginType === 'SSO Only' && this.ssoType !== 'ADFS' && emailControl?.invalid) {
      this.logAutoLoginOutcome(trigger, { ...context, reason: 'sso-only-email-invalid' });
      return;
    }
    if (this.forceSSO === SMS_OPTIONS.NO && this.useSSO === SMS_OPTIONS.NO && passwordControl?.invalid) {
      this.logAutoLoginOutcome(trigger, { ...context, reason: 'password-invalid' });
      return;
    }
    if (!this.captcha && this.isCaptchaEnabled && this.isCaptchaEnabled === SMS_OPTIONS.YES) {
      this.showCaptchaError = true;
      this.logAutoLoginOutcome(trigger, { ...context, reason: 'captcha-missing' });
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const redirectUrl = params?.get('redirectUrl');

    const payload = { 
      email: this.loginForm.controls.email.value,
      password: this.loginForm.controls.password.value,
      ...(redirectUrl && { deepLinkUrl: redirectUrl })
    };

    this.weds.showDialog({ type: DIALOG_TYPES.WAIT, title: "Checking", message: "Please wait while your credentials are verified" });
    const apiSubscription = this.apiService.postApi(API_ENDPOINT.LOGIN, payload).subscribe(
      (rs: any) => {
        apiSubscription.unsubscribe();
        this.weds.closeDialog();
        if (rs.status === RESPONSE_STATUS.SUCCESS && rs.action === LoginState.RESET_PASSWORD) {
          this.logAutoLoginOutcome(trigger, { ...this.summarizeAuthResponse(rs), branch: 'reset-password' });
          this.tempUserId = rs.tempUserId;
          this.tempClientId = rs.tempClientId;
          const oldPassword = this.loginForm.controls.password.value;
          this.resetMode = ResetMode.DEFAULT_PASSWORD;
          this.resetForm(LoginState.RESET_PASSWORD);
          setTimeout(() => {
            this.loginForm.controls.oldPassword.setValue(oldPassword);
          }, 150);
        } else {
          this.logAutoLoginOutcome(trigger, { ...this.summarizeAuthResponse(rs), branch: 'set-login-data' });
          if (this.clientService.setLoginData(rs)) {
            this.weds.showDialog({ type: DIALOG_TYPES.GENERIC, code: rs.error ? rs.error.code : 99 });
            if (rs.error.code === 160) this.state = LoginState.FORGOT_PASSWORD;
          }
        }
      },
      (err) => {
        this.logAutoLoginOutcome(trigger, {
          branch: 'http-error',
          status: err?.status ?? null,
          message: err?.message ?? null,
        }, 'error');
        this.weds.closeDialog();
        this.weds.showDialog({ type: DIALOG_TYPES.GENERIC, code: 0 });
      }
    );
  }

  private handleForgotPassword(): void {
    if (this.loginForm.controls.email.invalid) return;
    this.weds.showDialog({ type: DIALOG_TYPES.WAIT, title: 'Checking...', message: "Please wait while a one-time-password is emailed to you" });
    const apiSubscription = this.apiService.postApi(API_ENDPOINT.FORGOT_PASSWORD, { email: this.loginForm.controls.email.value }).subscribe(
      (rs: any) => {
        apiSubscription.unsubscribe();
        this.weds.closeDialog();
        if (rs.status === RESPONSE_STATUS.REDIRECT) {
          window.location.href = rs.location;
        } else if (rs.status === RESPONSE_STATUS.SUCCESS) {
          this.tempUserId = rs.tempUserId;
          this.tempClientId = rs.tempClientId;
          this.resetForm(LoginState.RESET_PASSWORD);
          this.weds.showDialog({ code: -8 });
        } else {
          this.weds.showDialog({ type: DIALOG_TYPES.GENERIC, code: rs.error ? rs.error.code : 99 });
          if (rs.error.code === 113) this.forceSSO = SMS_OPTIONS.YES;
        }
      },
      (err) => {
        this.weds.closeDialog();
        this.weds.showDialog({ type: DIALOG_TYPES.GENERIC, code: 0 });
      }
    );
  }

  private handleResetPassword(): void {
    let valid = true;
    [this.resetMode === 'otp' ? 'otp' : 'oldPassword', 'password', 'password2'].forEach((key) => {
      this.loginForm.controls[key].updateValueAndValidity();
      if (this.loginForm.controls[key].invalid) valid = false;
    });
    if (!valid) {
      return;
    }

    this.weds.showDialog({ type: DIALOG_TYPES.WAIT, title: 'Saving', message: "Please wait while your password is reset" });
    const apiSubscription = this.apiService.postApi(API_ENDPOINT.RESET_PASSWORD, {
      userId: this.tempUserId,
      clientId: this.tempClientId,
      resetMode: this.resetMode,
      otp: this.loginForm.controls.otp.value,
      oldPassword: this.loginForm.controls.oldPassword.value,
      password: this.loginForm.controls.password.value,
      password2: this.loginForm.controls.password2.value
    }).subscribe(
      (rs: any) => {
        apiSubscription.unsubscribe();
        this.weds.closeDialog();
        if (this.clientService.setLoginData(rs)) {
          this.weds.showDialog({ type: DIALOG_TYPES.GENERIC, code: rs.error ? rs.error.code : 99 });
          if (rs.error.code === 159) this.state = LoginState.FORGOT_PASSWORD;
          if (rs.error.code === 113) this.forceSSO = SMS_OPTIONS.YES;
        }
      },
      (err) => {
        this.weds.closeDialog();
        this.weds.showDialog({ type: DIALOG_TYPES.GENERIC, code: 0 });
      }
    );
  }

  resolved(captchaResponse: string): void {
    this.captcha = captchaResponse;
  }

  private initGoogleIdentity(): void {
    if (document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
      this.renderGoogleButton();
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://accounts.google.com/gsi/client';
    script.nonce = this.cspNonce;
    script.async = true;
    script.defer = true;
    script.onload = () => this.renderGoogleButton();
    script.onerror = () => console.error('Failed to load Google Sign-In script');
    document.head.appendChild(script);
  }

  private renderGoogleButton(): void {
    const nonce = this.cspNonce;

    if ((window as any).google?.accounts?.id) {
      (window as any).google.accounts.id.initialize({ client_id: this.oauthClientId, nonce, ux_mode: 'redirect',  login_uri: `${window.location.origin}${environment.apiPath}google-oauth`, context: 'signin', auto_prompt: false });
      (window as any).google.accounts.id.renderButton(this.googleDiv().nativeElement, { type: 'standard', shape: 'pill', theme: 'filled_blue', size: 'medium', logo_alignment: 'left' });
    } else {
      console.error('Google Sign-In library not loaded');
    }

    this.apiService.searchData('google-oauth', { setNonce: true, nonce }).subscribe();
  }

  initMicrosoftIdentity(): void {
    this.weds.showDialog({ type: DIALOG_TYPES.WAIT, title: 'Initialising...', message: 'Please wait while a single-sign-on is initiated' });
    const apiSubscription = this.apiService.searchData('microsoft-oauth', { initSSO: true }).subscribe(
      (rs: any) => {
        apiSubscription.unsubscribe();
        this.weds.closeDialog();
        if (rs.status === RESPONSE_STATUS.SUCCESS) {
          window.location.href = rs.url;
        } else {
          this.weds.showDialog({ type: DIALOG_TYPES.GENERIC, code: rs.error.code });
        }
      }
    );
  }

  // Additional methods for logic extraction
  showEmailField(): boolean {
    return this.state !== LoginState.RESET_PASSWORD && this.ssoType !== 'Google Identity' && this.ssoType !== 'Microsoft Identity';
  }

  showPasswordField(): boolean {
    return this.state !== LoginState.FORGOT_PASSWORD && this.useSSO === SMS_OPTIONS.NO && this.forceSSO === SMS_OPTIONS.NO;
  }

  getOldPasswordError(): string {
    const errors = this.loginForm.get('oldPassword').errors;
    return errors && errors.error ? errors.error : 'Invalid Password';
  }

  getPasswordLabel(): string {
    return this.state === LoginState.LOGIN ? 'Password' : 'New Password';
  }

  getPasswordError(): string {
    const errors = this.loginForm.get('password').errors;
    return errors && errors.error ? errors.error : 'Password is mandatory';
  }

  getPassword2Error(): string {
    const errors = this.loginForm.get('password2').errors;
    return errors && errors.error ? errors.error : 'Passwords must match';
  }

  showSSOCheckbox(): boolean {
    return this.forceSSO === SMS_OPTIONS.NO && this.state === LoginState.LOGIN && this.loginType !== 'Prismforce Password Only' && (this.ssoType === 'ADFS' || this.ssoType === 'LDAP');
  }

  showForgotPasswordButton(): boolean {
    return this.useSSO === SMS_OPTIONS.NO && this.forceSSO === SMS_OPTIONS.NO;
  }

  showCaptcha(): boolean {
    return this.isCaptchaEnabled === SMS_OPTIONS.YES && this.state !== LoginState.FORGOT_PASSWORD && this.useSSO === SMS_OPTIONS.NO && this.forceSSO === SMS_OPTIONS.NO;
  }

  showSignInButton(): boolean {
    return this.ssoType !== 'Google Identity' && this.ssoType !== 'Microsoft Identity';
  }

  getSubmitButtonLabel(): string {
    return this.state === LoginState.LOGIN ? 'Sign In' : this.state === LoginState.FORGOT_PASSWORD ? 'Reset Password' : 'Save Password';
  }

  showFirstTimeButton(): boolean {
    return this.state === LoginState.LOGIN && this.useSSO === SMS_OPTIONS.NO && this.forceSSO === SMS_OPTIONS.NO;
  }

  private readLoginTrace(): { seq: number; elapsedMs: number; navType: string } {
    if (typeof window === 'undefined') {
      return { seq: 0, elapsedMs: 0, navType: 'ssr' };
    }

    const seqKey = 'pf-login-trace-seq';
    const startKey = 'pf-login-trace-start';
    let seq = 1;
    let elapsedMs = 0;
    try {
      const next = Number(sessionStorage.getItem(seqKey) || '0') + 1;
      seq = Number.isFinite(next) && next > 0 ? next : 1;
      sessionStorage.setItem(seqKey, String(seq));
      const existingStart = Number(sessionStorage.getItem(startKey) || '0');
      const start = existingStart > 0 ? existingStart : Date.now();
      if (!existingStart) {
        sessionStorage.setItem(startKey, String(start));
      }
      elapsedMs = Date.now() - start;
    } catch {
      seq = 1;
      elapsedMs = 0;
    }

    const navEntry = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
    return { seq, elapsedMs, navType: navEntry?.type ?? 'unknown' };
  }

  private logLogin(step: string, data: Record<string, unknown> = {}, severity: CoralogixCustomLogSeverity = 'warn'): void {
    if (typeof window === 'undefined') {
      return;
    }

    const { seq, elapsedMs, navType } = this.loginTrace;
    const possibleLoop = seq >= 4 && elapsedMs > 0 && elapsedMs / seq < 5000;
    sendCoralogixCustomEvent(`[SPM-9676] login ${step}`, {
      step,
      seq,
      elapsedMs,
      navType,
      possibleLoop,
      path: window.location.pathname,
      referrerHost: this.safeHost(document.referrer),
      ...data,
    }, possibleLoop ? 'error' : severity);
  }

  private logAutoLoginOutcome(
    trigger: 'user' | 'seamless-sso' | 'url-params',
    data: Record<string, unknown>,
    severity: CoralogixCustomLogSeverity = 'warn'
  ): void {
    if (trigger === 'user') {
      return;
    }
    this.logLogin('login-outcome', { trigger, ...data }, severity);
  }

  private summarizeAuthResponse(rs: any): Record<string, unknown> {
    return {
      status: rs?.status ?? null,
      action: rs?.action ?? null,
      errorCode: rs?.error?.code ?? null,
      hasUser: Boolean(rs?.user),
      hasClient: Boolean(rs?.client),
      hasLocation: Boolean(rs?.location),
      hasRedirect: Boolean(rs?.redirect),
      locationHost: this.safeHost(rs?.location),
      redirectHost: this.safeHost(rs?.redirect),
    };
  }

  private safeHost(value: unknown): string | null {
    if (typeof value !== 'string' || !value || typeof window === 'undefined') {
      return null;
    }
    try {
      return new URL(value, window.location.origin).host;
    } catch {
      return null;
    }
  }
}
